import { useMemo } from 'react'
import AdditionalInfoPreview from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsAdditionalInfo/AdditionalInfoPreview'
import ContractDetailsDocuments from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsDocuments/ContractDetailsDocuments'
import ContractExtensionHistory from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsPreview/ContractExtensionHistory/ContractExtensionHistory'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { RowLayout } from '../../../MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { ViewNoteButton } from '../../ViewNote/ViewNoteButton'
import { ACTIONS, contractSubject, SUBJECTS } from '../../Abilities'
import { useContractDetailsAbility } from '../../Providers/ContractDetailsAbilityProvider'
import { Separator } from '../../../../components/Separator/Separator'
import { ResponsibleUsers } from '../ResponsibleUsers'
import { Container } from 'components/Container/Container'
import { Link } from '../../../../components/Link'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { defaultDateFormat } from 'helper/dates'
import { ReactComponent as Information } from 'assets/svg/Information.svg'
import { CONTRACT_STATUS, VENDOR_FEE_TYPES_VALUES } from '../../constants'
import { BTN_TXT, LABELS } from 'constants/txt'
import { routes } from '../../../../router'
import './styles.scss'
import { LedByInfo } from '../ContractDetailsAdditionalInfo/LedByInfo'
import UserList from '../ResponsibleUsers/UserList/UserList'

const extensionHistory = {
  title: 'Extension history',
  width: 675
}

const ContractDetailsPreview = () => {
  const contractDetailsContext = useContractDetailsContext()
  const ability = useContractDetailsAbility()

  const {
    details,
    canAllContractInfo,
    isOnboarded,
    paymentFrequencyOptions,
    canAddSignatory,
    canViewSignatory,
    canUploadAttachments,
    canUploadInitialDocument,
    canUploadNewDocumentVersion,
    canUploadNewDocument,
    canDownloadTemplates,
    canAcceptContract,
    canDownloadDocuments,
    vendorFeeTypeOptions,
    hasContractDocsArray,
    canDownloadAttachments,
    hasContractAttachmentsArray,
    shippingMethodOptions
  } = contractDetailsContext.state

  const isDocumentsVisible = useMemo(
    () =>
      canAddSignatory ||
      canViewSignatory ||
      canUploadAttachments ||
      canUploadInitialDocument ||
      canUploadNewDocumentVersion ||
      canUploadNewDocument ||
      canDownloadTemplates ||
      canAcceptContract ||
      (canDownloadAttachments && hasContractAttachmentsArray) ||
      (canDownloadDocuments && hasContractDocsArray),
    [
      canAcceptContract,
      canAddSignatory,
      canDownloadDocuments,
      canDownloadTemplates,
      canUploadAttachments,
      canUploadInitialDocument,
      canUploadNewDocumentVersion,
      canViewSignatory,
      hasContractDocsArray,
      canDownloadAttachments,
      canUploadNewDocument,
      hasContractAttachmentsArray
    ]
  )

  const hasExtensionHistory = !!details?.contract_extension_documents?.length

  const {
    state: { isVendor }
  } = useContractDetailsContext()

  return (
    <div className="contract-details-preview">
      <RowLayout title="Contract Details">
        <div className="contract-details-preview__grid gap-24">
          <FieldWithLabel
            title={
              <div className="row align-center gap-4">
                {LABELS.START_DATE}
                {isOnboarded && (
                  <Tooltip title={<div>Master agreement start date</div>}>
                    <Information />
                  </Tooltip>
                )}
              </div>
            }
          >
            <Typography.Body1>
              {defaultDateFormat(details.start_date) || '-'}
            </Typography.Body1>
          </FieldWithLabel>
          <Container.WithHistory
            hasVersionHistory={hasExtensionHistory}
            versionHistory={<ContractExtensionHistory />}
            className="finish-date-wrapper"
            versionHistoryButtonText={BTN_TXT.SHOW_EXTENSION_HISTORY}
            popupProps={extensionHistory}
          >
            <FieldWithLabel title={LABELS.FINISH_DATE}>
              <Typography.Body1>
                {details.status === CONTRACT_STATUS.TERMINATED &&
                !!details.termination_date
                  ? defaultDateFormat(details.termination_date)
                  : defaultDateFormat(details.finish_date) || '-'}
              </Typography.Body1>
            </FieldWithLabel>
          </Container.WithHistory>
          {isOnboarded ? (
            <FieldWithLabel title={LABELS.PRICING_EFFECTIVE_DATE}>
              <Typography.Body1>
                {details.pricing_effective_date
                  ? defaultDateFormat(details.pricing_effective_date)
                  : '-'}
              </Typography.Body1>
            </FieldWithLabel>
          ) : (
            <div />
          )}
        </div>
        <div className="contract-details-preview__grid gap-24">
          <LedByInfo details={details} />
          {details.contract_category_id && (
            <FieldWithLabel
              title="Contract category"
              className="add-contract-category__label"
            >
              <Link
                to={`${routes.contracts}${routes.contractCategories}/${details.contract_category_id}`}
              >
                <div className="mt-8">{details.contract_category_name}</div>
              </Link>
            </FieldWithLabel>
          )}
          <FieldWithLabel title={LABELS.SHIPPING_METHOD}>
            <Typography.Body1>
              {shippingMethodOptions.find(
                (item) => item.value === details.shipping_method
              )?.label || '-'}
            </Typography.Body1>
          </FieldWithLabel>
          <FieldWithLabel title={LABELS.CUSTOM_CONTRACT_NUMBER}>
            <Typography.Body1>{details.custom_number || '-'}</Typography.Body1>
          </FieldWithLabel>
          <ViewNoteButton
            title="Contract Note"
            canViewNote={ability.can(
              ACTIONS.VIEW,
              contractSubject(SUBJECTS.NOTE, details)
            )}
            canEditNote={ability.can(
              ACTIONS.EDIT,
              contractSubject(SUBJECTS.NOTE, details)
            )}
            note={details.note}
          />
        </div>
        {isVendor && (
          <>
            <Typography.Headline6 weight={TYPOGRAPHY_WEIGHT.BOLD}>
              Community contacts
            </Typography.Headline6>
            <UserList vendor={!isVendor} />
          </>
        )}
      </RowLayout>
      {canAllContractInfo && (
        <>
          <Separator />
          <RowLayout title="Fees & payment terms">
            <div className="contract-details-preview__grid gap-24">
              <FieldWithLabel title={LABELS.VENDOR_FEE_TYPE}>
                <Typography.Body1>
                  {vendorFeeTypeOptions.find(
                    (item) => item.value === details.vendor_fee_type
                  )?.label || '-'}
                </Typography.Body1>
              </FieldWithLabel>
              {details.vendor_fee_type !== VENDOR_FEE_TYPES_VALUES.NO_FEE && (
                <>
                  {details.vendor_fee_type ===
                    VENDOR_FEE_TYPES_VALUES.ADMIN && (
                    <FieldWithLabel title={LABELS.ADMIN_FEE}>
                      <Typography.Body1>{details.admin_fee}</Typography.Body1>
                    </FieldWithLabel>
                  )}
                  <FieldWithLabel
                    title={
                      details.vendor_fee_type ===
                      VENDOR_FEE_TYPES_VALUES.MARKETPLACE_AR
                        ? LABELS.MARKETPLACE_AR_PAYMENT_FREQUENCY
                        : LABELS.ADMIN_PAYMENT_FREQUENCY
                    }
                  >
                    <Typography.Body1>
                      {paymentFrequencyOptions.find(
                        (item) => item.value === details.payment_frequency
                      )?.label || '-'}
                    </Typography.Body1>
                  </FieldWithLabel>
                </>
              )}
              <FieldWithLabel title={LABELS.PAYMENT_TERM_DAYS}>
                <Typography.Body1>
                  {details.payment_term_days || '-'}
                </Typography.Body1>
              </FieldWithLabel>
            </div>
          </RowLayout>
        </>
      )}
      <Separator />
      <RowLayout title="Team Responsible">
        <AdditionalInfoPreview />
        <ResponsibleUsers />
      </RowLayout>
      {isDocumentsVisible && (
        <>
          <Separator />
          <RowLayout title="Documents">
            <ContractDetailsDocuments />
          </RowLayout>
        </>
      )}
    </div>
  )
}

export default ContractDetailsPreview
